import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { ArrowLeft, ShoppingBagIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { postToParent } from "../lib/shop-now-embed";
import { useWidgetParams } from "../hooks/use-widget-params";
import { useSafeBack } from "../hooks/use-safe-back";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useSetRebuyItems } from "@babackapp/return-widget-components/hooks/use-set-rebuy-items";

export function ShopNowPage() {
  const { orderId, shopId } = useWidgetParams();
  const { goBack } = useSafeBack(`/return-widget/${shopId}/order/${orderId}/summary`);
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);

  const t = useCustomText();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  // The embed and the banner run outside the widget's i18n: send them the
  // texts already translated, with the merchant's overrides (BBS-404). Amounts
  // are left as `{cart}`-style tokens for the banner to fill.
  const checkoutButton = t("shopNow.banner.checkoutButton");
  const creditToSpend = t("shopNow.banner.creditToSpend", { amount: "{amount}" });
  const creditLeft = t("shopNow.banner.creditLeft", { cart: "{cart}", remaining: "{remaining}" });
  const amountToPay = t("shopNow.banner.amountToPay", { cart: "{cart}", due: "{due}" });
  const continueLabel = t("shopNow.banner.continue");

  useEffect(() => {
    postToParent({
      type: "SHOP_NOW_PRELOAD",
      orderId,
      exchangeCreditAmount: financialSummary.exchangeTotal,
      currency: financialSummary.currency,
      taxMultiplier: financialSummary.taxMultiplier,
      lang,
      labels: { checkoutButton, creditToSpend, creditLeft, amountToPay, continue: continueLabel },
    });
  }, [
    orderId,
    financialSummary.exchangeTotal,
    financialSummary.currency,
    financialSummary.taxMultiplier,
    lang,
    checkoutButton,
    creditToSpend,
    creditLeft,
    amountToPay,
    continueLabel,
  ]);

  const setRebuyItems = useSetRebuyItems(orderId);

  const handleGoShopping = () => {
    setRebuyItems([]);
    postToParent({ type: "SHOP_NOW_START" });
  };

  const creditLabel =
    financialSummary.exchangeTotal > 0
      ? formatPrice(financialSummary.exchangeTotal, financialSummary.currency)
      : null;

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("shopNow.titleShopping")}</HeaderTitle>
        <HeaderDescription>
          {creditLabel
            ? t("shopNow.descriptionWithCredit", { amount: creditLabel })
            : t("shopNow.descriptionNoCredit")}
        </HeaderDescription>
      </Header>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer layoutId="line-item-back-button" disableVariants={{ exit: true }}>
          <Button variant="secondary" type="button" onClick={goBack}>
            <ArrowLeft />
            {t("common.back")}
          </Button>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer layoutId="shop-now-go" variants={{}}>
          <Button onClick={handleGoShopping}>
            <ShoppingBagIcon />
            {t("shopNow.ctaShopping")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
